import dayjs from "dayjs";

export function convertMinutesToFormattedTime(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = Math.round(totalMinutes % 60);
  return `${hours} Hr ${minutes}mins / day`;
}

export function averageIdlingTime(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);

  //const minutes = Math.round(totalMinutes % 60);
  return `${hours} Hr ${minutes} mins / day`;
}

export const formatTimePerDay = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);

  //const remainingMinutes = Math.round(minutes % 60);
  return `${hours} Hr ${minutes} mins / day`;
};

export const formatDistancePerDay = (distance: number): string => {
  return `${Math.round(distance)} Km / day`;
};

export const formatStartTime = (startTimeStr: string): string => {
  const fixedStr = startTimeStr.replace(/^(\d{4}-\d{2}-\d{2}):/, "$1T");
  const dateObj = new Date(fixedStr);

  const day = dateObj.getDate().toString().padStart(2, "0");
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = monthNames[dateObj.getMonth()];
  const year = dateObj.getFullYear();

  // Updated format: "27 Feb, 2025"
  const datePart = `${day} ${month}, ${year}`;
  const timePart = dateObj.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });

  return `${datePart}, ${timePart}`;
};

export const formatStartTimeWarning = (startTimeStr: string): string => {
  let formattedDateStr = startTimeStr;

  // Check if the date string contains underscores (format: YYYY_MM_DD_HH_MM_SS)
  if (startTimeStr.includes("_")) {
    const parts = startTimeStr.split("_");
    if (parts.length >= 6) {
      // Construct an ISO formatted string: YYYY-MM-DDTHH:MM:SS
      formattedDateStr = `${parts[0]}-${parts[1]}-${parts[2]}T${parts[3]}:${parts[4]}:${parts[5]}`;
    }
  } else {
    // For formats like "2025-02-22:23:58:54", replace the colon after the date with a "T"
    formattedDateStr = startTimeStr.replace(/^(\d{4}-\d{2}-\d{2}):/, "$1T");
  }

  const dateObj = new Date(formattedDateStr);

  // Get the day (without a leading zero)
  const day = dateObj.getDate().toString();
  // Create an array for abbreviated month names
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = monthNames[dateObj.getMonth()];
  const year = dateObj.getFullYear();

  // Format the date as "27 Feb, 2025"
  const datePart = `${day} ${month}, ${year}`;

  // Format the time (e.g., "03:54:41 PM")
  const timePart = dateObj.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });

  return `${datePart}, ${timePart}`;
};

export const extractNumericValue = (value: string): string => {
  const numericValue = value.match(/\d+(\.\d+)?/);
  return numericValue ? numericValue[0] : "";
};

export const convertDurationToSeconds = (duration: string): string => {
  const timeParts = duration.match(/(\d+)\s*(h|m|s)/g);
  if (!timeParts) return "";

  let totalSeconds = 0;
  timeParts.forEach((part) => {
    const value = parseInt(part.match(/\d+/)?.[0] || "0", 10);
    if (part.includes("h")) totalSeconds += value * 3600;
    else if (part.includes("m")) totalSeconds += value * 60;
    else if (part.includes("s")) totalSeconds += value;
  });

  return totalSeconds.toString();
};

export const formatDate = (inputDate: string | Date): string => {
  const date = new Date(inputDate);

  if (isNaN(date.getTime())) {
    throw new Error("Invalid date");
  }

  const day = date.getDate();
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();

  return `${day} ${month}, ${year}`;
};

export const formatTimeTo12Hour = (time24: string): string => {
  if (!time24) {
    throw new Error("Invalid time");
  }

  const [hoursStr, minutesStr] = time24.split(':');
  let hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);

  const period = hours >= 12 ? 'PM' : 'AM';

  hours = hours % 12 || 12; // Convert 0 to 12 for 12-hour format

  const formattedHours = hours.toString().padStart(2, '0');
  const formattedMinutes = minutes.toString().padStart(2, '0');

  return `${formattedHours}:${formattedMinutes} ${period}`;
};


export function formatDateTime(dateTimeStr: string): { date: string; time: string } {
  // Split the date_time string into parts
  const parts = dateTimeStr.split('_');

  if (parts.length !== 6) {
    throw new Error('Invalid date_time format. Expected format: YYYY_MM_DD_HH_MM_SS');
  }

  const [year, month, day, hour, minute, second] = parts;

  // Convert numeric month to abbreviated month name
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const monthIndex = parseInt(month, 10) - 1;

  if (monthIndex < 0 || monthIndex > 11) {
    throw new Error('Invalid month in date_time string');
  }

  const monthName = monthNames[monthIndex];

  // Format date as: 26Nov, 2024
  const formattedDate = `${parseInt(day)} ${monthName}, ${year}`;

  // Format time as: 12:18:04
  const formattedTime = `${hour}:${minute}:${second}`;

  return { date: formattedDate, time: formattedTime };
}

export const formatDateTimeNotification = (dateTimeStr: string): string => {
  // Replace the first colon with "T" to form a valid ISO date string.
  const isoString = dateTimeStr.replace(":", "T");
  const dateObj = new Date(isoString);
  const day = dateObj.getDate().toString().padStart(2, "0");
  const month = dateObj.toLocaleString("en-US", { month: "short" });
  const year = dateObj.getFullYear();
  const time = dateObj.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
  return `${day} ${month}, ${year}, ${time}`;
};

export const computeTimeDifference = (dateTimeStr: string): string => {
  const isoString = dateTimeStr.replace(":", "T");
  const notificationDate = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - notificationDate.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  if (diffSeconds < 60) return "Just now";
  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60)
    return `${diffMinutes} minute${diffMinutes === 1 ? "" : "s"} ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24)
    return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
};



export const dayCount = (dateTimeStr: string): number | string => {
  const inputDate = dateTimeStr ? dayjs(dateTimeStr) : null;
  const currentDate = dayjs(); // Get the current date
  const daysBetween = inputDate && inputDate.isValid()
    ? currentDate.diff(inputDate, "days") // Difference in days
    : "N/A";
  return daysBetween;
}




export function calculateDuration(
  departureDate: string,
  departureTime: string,
  arrivalDate: string,
  arrivalTime: string
): string {
  // Check if any required parameter is missing or empty
  if (!departureDate || !departureTime || !arrivalDate || !arrivalTime) {
    return "Error: Missing required datetime fields";
  }

  try {
    // Combine date and time strings into ISO format
    const departureDateTimeStr = `${departureDate}T${departureTime}`;
    const arrivalDateTimeStr = `${arrivalDate}T${arrivalTime}`;

    // Create Date objects
    const departureDateTime = new Date(departureDateTimeStr);
    const arrivalDateTime = new Date(arrivalDateTimeStr);

    // Validate dates
    if (isNaN(departureDateTime.getTime())) {
      return "Error: Invalid departure datetime format";
    }
    if (isNaN(arrivalDateTime.getTime())) {
      return "Error: Invalid arrival datetime format";
    }

    // Calculate duration in milliseconds
    const durationMs = arrivalDateTime.getTime() - departureDateTime.getTime();

    // Handle negative durations (arrival before departure)
    const isNegative = durationMs < 0;
    const absoluteMs = Math.abs(durationMs);

    // Convert to hours, minutes, seconds
    const totalSeconds = Math.floor(absoluteMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    // Format with leading zeros and optional negative sign
    const formattedDuration = `${isNegative ? '-' : ''}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

    return formattedDuration;
  } catch (error: unknown) {
    if (error instanceof Error) {
      return `Error: ${error.message}`;
    }
    return "Error: Unknown error occurred";
  }
}

export function formatTime(timeString: string) {
  const [hours, minutes] = timeString.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) {
    return "Invalid time format";
  }
  const formattedHours = hours.toString().replace(/^0+/, '');
  return formattedHours ? `${formattedHours} Hr ${minutes} mins` : `${minutes} mins`;
}




const getOrdinalSuffix = (day: number) => {
  if (day > 3 && day < 21) return `${day}th`;
  const suffixes = ["st", "nd", "rd"];
  const mod = day % 10;
  return `${day}${suffixes[mod - 1] || "th"}`;
};
export const formatDateInMonthYear = (date: string | null) => {
  if (!date) return "";
  const cleanedDate = date.replace(":", "T");
  const parsedDate = new Date(cleanedDate);

  if (isNaN(parsedDate.getTime())) return "";

  const day = parsedDate.getDate();
  const month = parsedDate.toLocaleString("en-US", { month: "short" });
  const year = parsedDate.getFullYear();

  return `${getOrdinalSuffix(day)} ${month}, ${year}`;
};



export const formateTimeFromSec = (sec: number) => {

  const hours = Math.floor(sec / 3600);
  const minutes = Math.floor((sec % 3600) / 60);
  const seconds = sec % 60;

  const formattedTime = `${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

  return formattedTime;
}

export const formatTimeTo24HourFormat = (input: string): string => {
  // Input: "2025-07-18:08:23:57"
  const [datePart, timePart] = input.split(":"); // This splits only at the first colon
  const timeString = input.substring(input.indexOf(":") + 1); // Extracts full time portion
  
  // Combine into proper ISO format: "2025-07-18T08:23:57"
  const isoString = `${datePart}T${timeString}`;
  const date = new Date(isoString);

  if (isNaN(date.getTime())) {
    return "Invalid Date";
  }

  // Format to 24-hour time: HH:mm:ss
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  return `${hours}:${minutes}:${seconds}`;
};